import { useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { getDeviceCategoryLabel, getEfemPeripheralReassignState } from '@cnapse/shared';
import type { DeviceInventoryStatus, TenantDeviceRecord } from '@cnapse/shared/schemas.js';
import { AppButton, AppTextField } from '../../design-system';
import { EfemSetupList, EfemSetupListEmpty, EfemSetupListRow } from './EfemSetupList';
import { EfemSetupSection } from './EfemSetupSection';
import type { HandheldScannerRow } from './efemGatewaySetup.types';

const HANDHELD_LABEL = getDeviceCategoryLabel('handheld-barcode-scanner');

interface HandheldScannerSectionProps {
  gatewaySerialNumber: string;
  row: HandheldScannerRow;
  onRowChange: (row: HandheldScannerRow) => void;
  allHandheldScanners: TenantDeviceRecord[];
  tenantGatewayStatusBySerial: ReadonlyMap<string, DeviceInventoryStatus | undefined>;
  disabled: boolean;
  loading?: boolean;
}

const EMPTY_FORM: HandheldScannerRow = {
  boundSerial: '',
  displayLabel: '',
};

function deviceSubtitle(serial: string, allHandheldScanners: TenantDeviceRecord[]): string | undefined {
  const device = allHandheldScanners.find((s) => s.serialNumber === serial);
  return device?.deviceModel ? device.deviceModel : undefined;
}

export function HandheldScannerSection({
  gatewaySerialNumber,
  row,
  onRowChange,
  allHandheldScanners,
  tenantGatewayStatusBySerial,
  disabled,
  loading = false,
}: HandheldScannerSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<HandheldScannerRow>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const isBound = !!row.boundSerial;

  const openAssignDialog = () => {
    setForm(isBound ? { ...row } : EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleDialogApply = () => {
    if (!form.boundSerial) {
      setFormError(`${HANDHELD_LABEL} device is required`);
      return;
    }

    const device = allHandheldScanners.find((s) => s.serialNumber === form.boundSerial);
    if (device) {
      const reassign = getEfemPeripheralReassignState({
        parentGatewaySerial: device.parentGatewaySerialNumber,
        currentGatewaySerial: gatewaySerialNumber,
        tenantGatewayStatusBySerial,
      });
      if (reassign.requiresMoveConfirm) {
        const fromGateway = device.parentGatewaySerialNumber ?? 'another gateway';
        if (
          !window.confirm(
            `${form.boundSerial} is currently commissioned on gateway ${fromGateway}. `
            + 'Move it to this gateway when you save?',
          )
        ) {
          return;
        }
      }
    }

    onRowChange({ ...form });
    setDialogOpen(false);
  };

  const handleClear = () => {
    if (!window.confirm(`Remove ${HANDHELD_LABEL.toLowerCase()} from this gateway?`)) return;
    onRowChange(EMPTY_FORM);
  };

  return (
    <EfemSetupSection
      title={HANDHELD_LABEL}
      statusLabel={isBound ? 'Assigned' : 'Not assigned'}
      statusVariant={isBound ? 'success' : 'warning'}
      subtitle="Pilot container binding — load port is chosen when scanning."
      disabled={disabled}
      disabledHint={`Enter a gateway name above to assign a ${HANDHELD_LABEL.toLowerCase()}.`}
      headerAction={
        !isBound ? (
          <AppButton size="small" variant="contained" startIcon={<Add />} onClick={openAssignDialog}>
            Assign
          </AppButton>
        ) : undefined
      }
    >
      {loading ? (
        <Box py={1}>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Loading…
          </Alert>
        </Box>
      ) : !isBound ? (
        <EfemSetupListEmpty
          message={`No ${HANDHELD_LABEL.toLowerCase()} assigned.`}
          action={
            <AppButton size="small" variant="outlined" startIcon={<Add />} onClick={openAssignDialog}>
              Assign scanner
            </AppButton>
          }
        />
      ) : (
        <EfemSetupList>
          <EfemSetupListRow
            primary={row.displayLabel || row.boundSerial}
            secondary={row.displayLabel ? row.boundSerial : undefined}
            meta={deviceSubtitle(row.boundSerial, allHandheldScanners)}
            onEdit={openAssignDialog}
            editLabel={`Change ${HANDHELD_LABEL.toLowerCase()}`}
            onDelete={handleClear}
            deleteLabel={`Remove ${HANDHELD_LABEL.toLowerCase()}`}
            isLast
          />
        </EfemSetupList>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isBound ? `Edit ${HANDHELD_LABEL.toLowerCase()}` : `Assign ${HANDHELD_LABEL.toLowerCase()}`}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
            <FormControl size="small" fullWidth required>
              <InputLabel>{HANDHELD_LABEL}</InputLabel>
              <Select
                label={HANDHELD_LABEL}
                value={form.boundSerial}
                onChange={(e) => setForm((prev) => ({ ...prev, boundSerial: e.target.value }))}
              >
                <MenuItem value="" disabled>
                  {`Select a ${HANDHELD_LABEL.toLowerCase()}`}
                </MenuItem>
                {allHandheldScanners.map((s) => {
                  const reassign = getEfemPeripheralReassignState({
                    parentGatewaySerial: s.parentGatewaySerialNumber,
                    currentGatewaySerial: gatewaySerialNumber,
                    tenantGatewayStatusBySerial,
                  });
                  return (
                    <MenuItem key={s.serialNumber} value={s.serialNumber}>
                      {s.serialNumber}
                      {s.deviceModel ? ` (${s.deviceModel})` : ''}
                      {s.serialNumber === row.boundSerial ? '' : reassign.assignmentLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <AppTextField
              label="Display label (optional)"
              size="small"
              value={form.displayLabel}
              onChange={(e) => setForm((prev) => ({ ...prev, displayLabel: e.target.value }))}
              fullWidth
              placeholder="Handheld scanner"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setDialogOpen(false)}>Cancel</AppButton>
          <AppButton variant="contained" onClick={handleDialogApply}>
            {isBound ? 'Apply' : 'Assign'}
          </AppButton>
        </DialogActions>
      </Dialog>
    </EfemSetupSection>
  );
}
